import { useState } from 'react'
import { api } from '../api/client'
import type { DayMetric, DayMovement, DaySummary, DaybookResponse, LivenessReport } from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner } from '../components/Banner'
import { PulseRecap } from '../components/PulseStrip'
import { deltaTone, formatChange, formatDate, formatMetric, metricLabel } from '../format'

interface Props {
  propertyId?: string
  onOpenMetrics: (end: string) => void
  onOpenFunnel: (name: string, reportDate: string) => void
  onOpenBriefing: (reportDate: string) => void
  onOpenPulse: () => void
}

const ARROW = { up: '↑', down: '↓', flat: '→' } as const

const STATUS_LABEL = {
  ok: 'All clear',
  attention: 'Needs attention',
  no_data: 'No data',
} as const

/** How many days the stream asks for, and how many a backfill builds in one go. */
const STREAM_DAYS = 30
const BACKFILL_DAYS = 14

/**
 * The daybook — the home screen.
 *
 * The unit here is a day, not a date range: the most recent day is expanded, and every
 * day behind it collapses to its verdict and a few figures. Only the leading entry is
 * computed on demand; the rest are read from the store, which is why scrolling back a
 * month costs nothing.
 *
 * Nothing on this page is phrased twice. The headline is the `summary` string the
 * agent received and the attention lines are the watches' own `reason` strings, so the
 * screen and the briefing cannot drift apart.
 */
export function Daybook({
  propertyId,
  onOpenMetrics,
  onOpenFunnel,
  onOpenBriefing,
  onOpenPulse,
}: Props) {
  const [reloadKey, setReloadKey] = useState(0)
  const [backfilling, setBackfilling] = useState(false)
  const [backfillNote, setBackfillNote] = useState<string | null>(null)
  // Off by default because it multiplies the cost: a funnel is a step query plus one
  // per breakdown, per day. Offered at all because without it the funnel page's
  // history strip has no way to fill except one day at a time.
  const [withFunnels, setWithFunnels] = useState(false)

  const { data, error, loading } = useAsync<DaybookResponse>(
    (signal) => api.days({ limit: STREAM_DAYS, propertyId }, signal),
    [propertyId, reloadKey],
    true,
    `days:${propertyId ?? ''}:${reloadKey}`,
  )

  // Fetched alongside the stream rather than inside it: the pulse reads unsettled hours
  // and the daybook reads settled days, and a failure of one must not blank the other.
  // Only the verdict is used here — the figures live on the Pulse tab — but it is still
  // fetched, because a tag that stopped firing is worth knowing without opening a tab
  // for it. The cache key matches the Pulse tab's unrefreshed one, so moving between the
  // two paints immediately, and the server's five-minute memo means the revalidation
  // behind it spends no GA4 quota.
  const pulse = useAsync<LivenessReport>(
    (signal) => api.pulse({ propertyId }, signal),
    [propertyId],
    true,
    `pulse:${propertyId ?? ''}:0`,
  )

  async function backfill() {
    setBackfilling(true)
    setBackfillNote(null)
    try {
      const result = await api.backfillDays({
        days: BACKFILL_DAYS,
        includeFunnels: withFunnels,
        propertyId,
      })
      setBackfillNote(
        result.built === 0
          ? 'Nothing new to record.'
          : `Recorded ${result.built} ${result.built === 1 ? 'day' : 'days'}.` +
              (result.failed ? ` ${result.failed} could not be built.` : ''),
      )
      setReloadKey((key) => key + 1)
    } catch (cause: unknown) {
      setBackfillNote(cause instanceof Error ? cause.message : 'The backfill failed.')
    } finally {
      setBackfilling(false)
    }
  }

  if (loading) {
    return (
      <div className="card">
        <p className="placeholder">Building today’s entry… (an unrecorded day fetches from GA4)</p>
      </div>
    )
  }
  if (error) return <Banner tone="critical">{error}</Banner>
  if (!data || data.days.length === 0) return null

  const [today, ...history] = data.days

  return (
    <div className="daybook">
      {/* Above the stream because a tag that stopped firing is more urgent than
          yesterday's numbers, and outside `daybook__entry` because it is not a day. One
          line only: the provisional figures belong on the Pulse tab, where they cannot
          be read alongside settled days. */}
      {pulse.data && <PulseRecap report={pulse.data} onOpen={onOpenPulse} />}

      <Today
        day={today}
        onOpenMetrics={onOpenMetrics}
        onOpenFunnel={onOpenFunnel}
        onOpenBriefing={onOpenBriefing}
      />

      {history.map((day) => (
        <Earlier key={day.report_date} day={day} onOpen={() => onOpenBriefing(day.report_date)} />
      ))}

      <div className="daybook__date" />
      <div className="daybook__entry daybook__entry--tail">
        {history.length === 0 ? (
          <p className="section__note">
            No earlier days are recorded yet. Each day is written down the first time it is
            built, so the stream fills in from here — or record the last {BACKFILL_DAYS} at once.
          </p>
        ) : (
          <p className="section__note">
            {data.recorded} earlier {data.recorded === 1 ? 'day' : 'days'} recorded.
          </p>
        )}
        <div className="daybook__tail-actions">
          <button type="button" className="button secondary" onClick={backfill} disabled={backfilling}>
            {backfilling ? 'Recording…' : `Record the last ${BACKFILL_DAYS} days`}
          </button>
          <label className="backfill__option">
            <input
              type="checkbox"
              checked={withFunnels}
              onChange={(event) => setWithFunnels(event.target.checked)}
            />
            Include funnels
          </label>
        </div>
        {/* Said plainly rather than buried in a tooltip: this is the one control here
            that spends GA4 quota, and how much depends on the property. */}
        <span className="section__note">
          Builds one check and one report per missing day
          {withFunnels
            ? ', plus a step query and one per breakdown for each configured funnel — several times the cost.'
            : '. Funnels are left out, so the funnel history strip fills in a day at a time.'}
        </span>
        {backfillNote && <p className="section__note">{backfillNote}</p>}
      </div>
    </div>
  )
}

function Today({
  day,
  onOpenMetrics,
  onOpenFunnel,
  onOpenBriefing,
}: {
  day: DaySummary
  onOpenMetrics: (end: string) => void
  onOpenFunnel: (name: string, reportDate: string) => void
  onOpenBriefing: (reportDate: string) => void
}) {
  const [weekday, dayMonth] = splitDate(day.report_date)

  return (
    <>
      <div className="daybook__date">
        <span className="daybook__weekday">{weekday}</span>
        <span className="daybook__daymonth">{dayMonth}</span>
        <span className="daybook__today">Most recent</span>
      </div>

      <article className={`daybook__entry daybook__entry--lead daybook__entry--${day.status}`}>
        <div className="daybook__eyebrow">
          <span className={`daybook__status daybook__status--${day.status}`}>
            {STATUS_LABEL[day.status]}
          </span>
          <span className="section__note">
            {day.checks_attention} of {day.checks_total} watches tripped
            {day.comparison_date && ` · compared with ${formatDate(day.comparison_date, 'long')}`}
          </span>
        </div>

        <h1 className="daybook__headline">{day.summary}</h1>

        {!day.safe_to_interpret && (
          <Banner tone="critical">
            <strong>Data quality: {day.data_quality_status}.</strong> These figures describe the
            collection, not the audience — treat them as unreliable.
          </Banner>
        )}

        {day.attention.length > 0 && (
          <ul className="daybook__reasons">
            {day.attention.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}

        {day.metrics.length > 0 && (
          <div className="daybook__strip">
            {day.metrics.map((metric) => (
              <MetricCell key={metric.metric} metric={metric} />
            ))}
          </div>
        )}

        {day.movements.length > 0 && (
          <>
            <h2 className="daybook__section">What moved</h2>
            <Movements movements={day.movements} />
          </>
        )}

        <div className="daybook__actions">
          <button type="button" className="link-button" onClick={() => onOpenMetrics(day.report_date)}>
            Chart the 7 days to here →
          </button>
          {day.funnels.map((funnel) => (
            <button
              key={funnel.name}
              type="button"
              className="link-button"
              onClick={() => onOpenFunnel(funnel.name, day.report_date)}
            >
              {/* Operators name their own funnels, and plenty call one "Purchase
                  funnel" already — appending the word unconditionally produced
                  "the purchase funnel funnel". */}
              Open the {funnel.label.toLowerCase()}
              {/funnel\s*$/i.test(funnel.label) ? '' : ' funnel'} →
            </button>
          ))}
          <button
            type="button"
            className="link-button link-button--quiet"
            onClick={() => onOpenBriefing(day.report_date)}
          >
            Evidence package
          </button>
        </div>
      </article>
    </>
  )
}

function Earlier({ day, onOpen }: { day: DaySummary; onOpen: () => void }) {
  const [weekday, dayMonth] = splitDate(day.report_date)
  // The two figures a collapsed row can carry without becoming a second dashboard.
  const headline = day.metrics.slice(0, 2)

  return (
    <>
      <div className="daybook__date daybook__date--quiet">
        <span className="daybook__weekday">{weekday}</span>
        <span className="daybook__daymonth">{dayMonth}</span>
      </div>

      <article className={`daybook__entry daybook__entry--brief daybook__entry--${day.status}`}>
        <button type="button" className="daybook__row" onClick={onOpen}>
          <span className={`daybook__dot daybook__dot--${day.status}`} aria-hidden="true" />
          <span className="daybook__row-status">{STATUS_LABEL[day.status]}</span>
          <span className="daybook__row-summary">
            {day.safe_to_interpret ? day.summary : 'Data quality failed — figures suppressed'}
          </span>
          {day.safe_to_interpret && (
            <span className="daybook__row-metrics">
              {headline.map((metric) => (
                <span key={metric.metric}>
                  {formatMetric(metric.metric, metric.current)}{' '}
                  <span className={`tile__delta--${deltaTone(metric.change_percent, metric.metric)}`}>
                    {formatChange(metric.change_percent)}
                  </span>
                </span>
              ))}
            </span>
          )}
        </button>
      </article>
    </>
  )
}

function MetricCell({ metric }: { metric: DayMetric }) {
  const tone = deltaTone(metric.change_percent, metric.metric)
  return (
    <div className="daybook__cell">
      <span className="daybook__cell-label">{metricLabel(metric.metric)}</span>
      <span className="daybook__cell-value" title={formatMetric(metric.metric, metric.current, 'full')}>
        {formatMetric(metric.metric, metric.current)}
      </span>
      <span className={`daybook__cell-delta tile__delta--${tone}`}>
        <span aria-hidden="true">{ARROW[tone]}</span> {formatChange(metric.change_percent)}
      </span>
    </div>
  )
}

function Movements({ movements }: { movements: DayMovement[] }) {
  // Bars are scaled against the largest movement on the day, not against a fixed
  // percentage: the row is there to rank, and a fixed scale would flatten a quiet day
  // into nothing and clip a dramatic one.
  const widest = Math.max(...movements.map((item) => Math.abs(item.change_percent)), 1)

  return (
    <div className="movements">
      {movements.map((movement) => {
        const tone = deltaTone(movement.change_percent, movement.metric)
        return (
          <div className="movement" key={`${movement.metric}-${movement.segment}`}>
            <span className="movement__segment">{movement.segment}</span>
            <span className="movement__track">
              <span
                className={`movement__bar movement__bar--${tone}`}
                style={{ width: `${(Math.abs(movement.change_percent) / widest) * 100}%` }}
              />
            </span>
            <span className="movement__detail">
              {metricLabel(movement.metric)} {formatMetric(movement.metric, movement.previous, 'full')} →{' '}
              {formatMetric(movement.metric, movement.current, 'full')}
            </span>
            <span className={`movement__change tile__delta--${tone}`}>
              <span aria-hidden="true">{ARROW[tone]}</span> {formatChange(movement.change_percent)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * "Friday" / "21 August" for the gutter.
 *
 * Built from the ISO parts rather than a `Date`, for the same reason `formatDate` is:
 * `new Date('2026-08-21')` is UTC midnight and renders as the 20th west of Greenwich.
 */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function splitDate(iso: string): [string, string] {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return [iso, '']
  // Constructed in UTC and read in UTC, so the weekday is the one the date names.
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? ''
  return [weekday, `${day} ${MONTHS[month - 1] ?? ''}`]
}
